import { useCallback, useRef, useState } from "react";
import { toast } from "~/components/ui/toaster";
import { Card } from "~/components/ui/card";
import { Icon } from "~/components/ui/icons/icon";
import { Separator } from "~/components/ui/separator";

interface POIContextMenuProps {
  name: string;
  onAddPlace: () => void;
}

export function POIContextMenu({ name, onAddPlace }: POIContextMenuProps) {
  const [copied, setCopied] = useState(false);
  const [activeIndex, setActiveIndex] = useState(1); // Start on "Add to map"
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);

  const handleCopyName = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(name);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  }, [name]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const itemCount = 2;

      switch (e.key) {
        case "ArrowDown":
        case "ArrowRight":
          e.preventDefault();
          setActiveIndex((prev) => {
            const next = (prev + 1) % itemCount;
            itemsRef.current[next]?.focus();
            return next;
          });
          break;
        case "ArrowUp":
        case "ArrowLeft":
          e.preventDefault();
          setActiveIndex((prev) => {
            const next = (prev - 1 + itemCount) % itemCount;
            itemsRef.current[next]?.focus();
            return next;
          });
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (activeIndex === 0) handleCopyName();
          else onAddPlace();
          break;
      }
    },
    [activeIndex, onAddPlace, handleCopyName],
  );

  const itemClass =
    "w-full flex items-center gap-3 px-2 py-1.5 rounded-md text-sm hover:bg-accent transition-colors text-left cursor-pointer focus:outline-none focus:bg-accent";

  return (
    <Card elevation={2} className="p-2 min-w-48">
      <div role="menu" aria-label="Place options" onKeyDown={handleKeyDown}>
        <div
          ref={(el) => (itemsRef.current[0] = el)}
          role="menuitem"
          tabIndex={activeIndex === 0 ? 0 : -1}
          onClick={handleCopyName}
          onFocus={() => setActiveIndex(0)}
          className={itemClass}
        >
          <Icon i={copied ? "check" : "copy"} className="size-4 text-muted-foreground" />
          <span className="font-medium text-foreground truncate">{copied ? "Copied!" : name}</span>
        </div>

        <Separator className="my-1.5" />

        <div
          ref={(el) => {
            itemsRef.current[1] = el;
            // Auto-focus on mount
            if (el && activeIndex === 1) el.focus();
          }}
          role="menuitem"
          tabIndex={activeIndex === 1 ? 0 : -1}
          onClick={onAddPlace}
          onFocus={() => setActiveIndex(1)}
          className={itemClass}
        >
          <Icon i="add-place" className="size-4 text-muted-foreground" />
          Add to adventure map
        </div>
      </div>
    </Card>
  );
}
